import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { TicketPriority, TicketStatus } from 'helpers/Enums';
import { useHistory } from 'react-router-dom';
import { appUserContext } from 'helpers/services';
import moment from 'moment';
import { LocalizationIds } from 'helpers/Localizations';
import { useIntl } from 'react-intl';


interface ICustomerNewTicketContext {
    isLoading: boolean
    newTicketSchema: any
    reporters: IReporterUser[]
    selectedOrders: ITicketOrder[]
    selectedWorkOrders: ITicketWorkOrder[]

    saveNewTicket: (values: any) => void
    orderSelectionHandler: (order: ITicketOrder) => void
    workOrderSelectionHandler: (workOrder: ITicketWorkOrder) => void
}

interface ITicketOrder {
    orderId: number
    orderNumber: string
    orderDate?: moment.Moment
    orderType: string
    orderTitle: string
}

interface ITicketWorkOrder {
    workOrderId: number
    workOrderNumber: string
    articleId: string
    articleModel: string
    articleVersion: string
    articleDescription: string
    orderId?: number
    orderNumber: string
    orderDate?: moment.Moment
    orderType: string
    orderTitle: string
}

interface IReporterUser {
    value: string
    label: string
    isExternal: boolean
    newUser: boolean
}

export let customerNewTicketContext = React.createContext({} as ICustomerNewTicketContext);

let { Provider } = customerNewTicketContext;

let CustomerNewTicketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();

    let { formatMessage } = useIntl();

    let newTicketSchema = yup.object({
        title: yup.string().default('')
            .required(formatMessage(
                { id: LocalizationIds.RequiredField },
                { field: formatMessage({ id: LocalizationIds.Title }) })
            ),
        status: yup.string().default(TicketStatus.Backlog),
        customerId: yup.number().default(0).nullable(),
        customerName: yup.string().default(''),
        customerCountryCode: yup.string().default(''),
        customerCountryName: yup.string().default(''),
        ticketSubCategoryId: yup.string().default('')
            .required(formatMessage(
                { id: LocalizationIds.RequiredField },
                { field: formatMessage({ id: LocalizationIds.Category }) })
            ),
        reporterUserName: yup.string().default('').nullable(),
        priority: yup.string().default(TicketPriority.Normal).nullable(),
        workOrders: yup.array().of(yup.string()).default([]),
        orders: yup.array().default([]),
        message: yup.string().default('').required(formatMessage({ id: LocalizationIds.PleaseSetMessage }))
    });

    type NewTicket = yup.InferType<typeof newTicketSchema>;

    let [isLoading, setLoading] = useState(false);

    let { currentUser } = useContext(appUserContext);

    let [reporters, setReporters] = useState<IReporterUser[]>([]);
    let [selectedOrders, setSelectedOrders] = useState<ITicketOrder[]>([]);
    let [selectedWorkOrders, setSelectedWorkOrders] = useState<ITicketWorkOrder[]>([]);


    useEffect(() => {
        let getReporters = async () => {
            try {
                let request = {
                    customerId: currentUser.customerClaims?.customerId
                };

                let res = await axios.get('/api/User/GetReporterUsers', { params: request });
                setReporters(res.data.items);

            } catch { }
        }

        getReporters();
    }, []);


    let saveNewTicket = async (values: NewTicket) => {
        try {
            setLoading(true);

            let request = {
                ...values,
                orders: selectedOrders,
                workOrders: selectedWorkOrders
            }

            let res = await axios.post('/api/CustomerTicket/SaveNewTicket', request);

            if (res.data.affectedRecords === 1) {
                enqueueSnackbar("Ticket salvato", { variant: "success" });
                history.push(`/customer/ticket/edit/${res.data.stringId}`);
            }

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let orderSelectionHandler = (order: ITicketOrder) => {
        let idx = selectedOrders.findIndex(f => f.orderId === order.orderId);

        if (idx !== -1)
            setSelectedOrders([...selectedOrders.filter(f => f.orderId !== order.orderId)]);
        else {
            selectedOrders.push(order);
            setSelectedOrders([...selectedOrders]);
        }

    }

    let workOrderSelectionHandler = (workOrder: ITicketWorkOrder) => {
        let idx = selectedWorkOrders.findIndex(x => x.workOrderId === workOrder.workOrderId);

        if (idx !== -1)
            setSelectedWorkOrders([...selectedWorkOrders.filter(x => x.workOrderId !== workOrder.workOrderId)]);
        else {
            selectedWorkOrders.push(workOrder);
            setSelectedWorkOrders([...selectedWorkOrders]);
        }
    }

    return (
        <Provider value={{
            isLoading,
            newTicketSchema, 
            reporters,
            selectedOrders,
            selectedWorkOrders,

            saveNewTicket,
            orderSelectionHandler,
            workOrderSelectionHandler
        }}>
            {children}
        </Provider>
    );
}

export default CustomerNewTicketProvider;